import { CompanyInfo, ContractedCompany, InventoryItem, ServiceTicket, StockMovement, AppUser } from '../types';

export interface FullDatabaseBackup {
  company: CompanyInfo;
  users: AppUser[];
  deviceTypes: string[];
  deviceBrands: string[];
  inventoryCategories: string[];
  inventory: InventoryItem[];
  stockMovements: StockMovement[];
  tickets: ServiceTicket[];
  contractedCompanies: ContractedCompany[];
  labelConfig?: any;
  smsSettings?: any;
  smsLogs?: any[];
  exchangeRate?: any;
}

function escapeSqlString(str: any): string {
  if (str === null || str === undefined) return 'NULL';
  const s = String(str);
  return "'" + s.replace(/[\0\x08\x09\x1a\n\r"'\\\%]/g, (char) => {
    switch (char) {
      case '\0': return '\\0';
      case '\x08': return '\\b';
      case '\x09': return '\\t';
      case '\x1a': return '\\z';
      case '\n': return '\\n';
      case '\r': return '\\r';
      case '"':
      case "'":
      case '\\':
      case '%':
        return '\\' + char;
      default:
        return char;
    }
  }) + "'";
}

function escapeSqlNumber(num: any, fallback = 0): string {
  if (num === null || num === undefined || num === '') return String(fallback);
  const n = Number(num);
  return isNaN(n) ? String(fallback) : String(n);
}

function escapeSqlDate(dateStr: any): string {
  if (!dateStr) return 'NULL';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'NULL';
    const pad = (n: number) => (n < 10 ? '0' + n : String(n));
    return `'${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}'`;
  } catch {
    return 'NULL';
  }
}

function getTicketCalculatedTotal(t: any): number {
  if (t.totalAmount !== undefined && Number(t.totalAmount) > 0) {
    return Number(t.totalAmount);
  }
  const parts = Array.isArray(t.parts) ? t.parts : [];
  const partsSum = parts.reduce((sum: number, p: any) => {
    const qtyValue = Number(p?.quantity);
    const qty = Number.isFinite(qtyValue) ? Math.max(0, qtyValue) : 1;
    const price = Number(p?.unitPrice) || 0;
    return sum + (price * qty);
  }, 0);
  const labor = Number(t.laborCost) || 0;
  const discount = Number(t.discount) || 0;
  const rawSum = Math.max(0, partsSum + labor - discount);
  const vatRate = Math.max(0, Number(t.vatRate) || 0);
  const roundingAdjustment = Number(t.roundingAdjustment) || 0;
  let total = rawSum;
  if (vatRate > 0 && !t.isVatIncluded) {
    total += rawSum * vatRate / 100;
  }
  return Math.max(0, Math.round((total + roundingAdjustment) * 100) / 100);
}

function getTicketPayments(t: any): { cash: number; card: number; transfer: number; total: number } {
  let cash = 0;
  let card = 0;
  let transfer = 0;

  if (Array.isArray(t.payments) && t.payments.length > 0) {
    t.payments.forEach((p: any) => {
      const amt = Number(p?.amount) || 0;
      if (amt <= 0) return;
      const method = String(p?.method || '').toLowerCase();
      if (method.includes('kart') || method.includes('pos') || method.includes('card')) {
        card += amt;
      } else if (method.includes('havale') || method.includes('eft') || method.includes('bank') || method.includes('transfer')) {
        transfer += amt;
      } else {
        cash += amt;
      }
    });
    return { cash, card, transfer, total: cash + card + transfer };
  }

  if (t.paymentBreakdown && typeof t.paymentBreakdown === 'object') {
    cash = Number(t.paymentBreakdown.cash) || 0;
    card = Number(t.paymentBreakdown.card) || 0;
    transfer = Number(t.paymentBreakdown.transfer) || 0;
    return { cash, card, transfer, total: cash + card + transfer };
  }

  const paid = Number(t.paidAmount) || (t.paymentStatus === 'Odendi' ? getTicketCalculatedTotal(t) : 0);
  if (paid > 0) {
    const method = String(t.paymentMethod || '').toLowerCase();
    if (method.includes('kart') || method.includes('pos') || method.includes('card')) {
      return { cash: 0, card: paid, transfer: 0, total: paid };
    } else if (method.includes('havale') || method.includes('eft') || method.includes('bank')) {
      return { cash: 0, card: 0, transfer: paid, total: paid };
    }
    return { cash: paid, card: 0, transfer: 0, total: paid };
  }

  return { cash: 0, card: 0, transfer: 0, total: 0 };
}

export function generateSqlDump(data: FullDatabaseBackup): string {
  const now = new Date();
  const dateStr = now.toISOString().replace('T', ' ').slice(0, 19);

  const safeTickets = data.tickets || [];
  const safeInventory = data.inventory || [];
  const safeStockMovements = data.stockMovements || [];
  const safeContracted = data.contractedCompanies || [];
  const safeUsers = data.users || [];
  const safeSmsLogs = data.smsLogs || [];

  let sql = '';
  sql += `-- ========================================================\n`;
  sql += `-- TEKNİK SERVİS YÖNETİM SİSTEMİ - MYSQL / PHPMYADMIN VERİTABANI YEDEĞİ\n`;
  sql += `-- Yedek Alma Tarihi: ${dateStr}\n`;
  sql += `-- Toplam Servis Kaydı: ${safeTickets.length}\n`;
  sql += `-- Toplam Stok/Envanter: ${safeInventory.length}\n`;
  sql += `-- Toplam Stok Hareketi: ${safeStockMovements.length}\n`;
  sql += `-- Toplam Anlaşmalı Firma: ${safeContracted.length}\n`;
  sql += `-- Toplam Personel/Kullanıcı: ${safeUsers.length}\n`;
  sql += `-- ========================================================\n\n`;

  sql += `SET FOREIGN_KEY_CHECKS=0;\n`;
  sql += `SET SQL_MODE = "NO_AUTO_VALUE_ON_ZERO";\n`;
  sql += `SET NAMES utf8mb4;\n`;
  sql += `START TRANSACTION;\n`;
  sql += `SET time_zone = "+00:00";\n\n`;

  // 1. TICKETS TABLE
  sql += `-- --------------------------------------------------------\n`;
  sql += `-- 1. Servis Kayıtları Ana Tablosu (\`tickets\`)\n`;
  sql += `-- --------------------------------------------------------\n`;
  sql += `CREATE TABLE IF NOT EXISTS \`tickets\` (
  \`id\` VARCHAR(64) NOT NULL,
  \`customer_name\` VARCHAR(255) NULL,
  \`customer_phone\` VARCHAR(64) NULL,
  \`customer_email\` VARCHAR(255) NULL,
  \`customer_address\` TEXT NULL,
  \`device_type\` VARCHAR(100) NULL,
  \`device_brand\` VARCHAR(100) NULL,
  \`device_model\` VARCHAR(255) NULL,
  \`serial_number\` VARCHAR(100) NULL,
  \`issue\` TEXT NULL,
  \`accessories\` TEXT NULL,
  \`physical_condition\` TEXT NULL,
  \`notes\` TEXT NULL,
  \`status\` VARCHAR(50) DEFAULT 'Kaydedildi',
  \`service_type\` VARCHAR(50) DEFAULT 'Ucretli',
  \`stage\` VARCHAR(50) DEFAULT 'intake',
  \`total_price\` DECIMAL(12, 2) DEFAULT 0.00,
  \`labor_cost\` DECIMAL(12, 2) DEFAULT 0.00,
  \`discount\` DECIMAL(12, 2) DEFAULT 0.00,
  \`rounding_adjustment\` DECIMAL(12, 2) DEFAULT 0.00,
  \`vat_rate\` DECIMAL(5, 2) DEFAULT 0.00,
  \`is_vat_included\` TINYINT(1) DEFAULT 0,
  \`assigned_technician_id\` VARCHAR(64) NULL,
  \`assigned_technician_name\` VARCHAR(100) NULL,
  \`is_contracted\` TINYINT(1) DEFAULT 0,
  \`contracted_company_id\` VARCHAR(64) NULL,
  \`contracted_company_name\` VARCHAR(255) NULL,
  \`payment_status\` VARCHAR(50) DEFAULT 'Bekliyor',
  \`cash_amount\` DECIMAL(12, 2) DEFAULT 0.00,
  \`card_amount\` DECIMAL(12, 2) DEFAULT 0.00,
  \`transfer_amount\` DECIMAL(12, 2) DEFAULT 0.00,
  \`delivery_date\` DATETIME NULL,
  \`created_at\` DATETIME NULL,
  \`updated_at\` DATETIME NULL,
  \`data_json\` LONGTEXT NULL,
  PRIMARY KEY (\`id\`),
  INDEX \`idx_tickets_status\` (\`status\`),
  INDEX \`idx_tickets_customer_phone\` (\`customer_phone\`),
  INDEX \`idx_tickets_created_at\` (\`created_at\`)
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;\n\n`;

  if (safeTickets.length > 0) {
    sql += `INSERT INTO \`tickets\` (
  \`id\`, \`customer_name\`, \`customer_phone\`, \`customer_email\`, \`customer_address\`,
  \`device_type\`, \`device_brand\`, \`device_model\`, \`serial_number\`, \`issue\`,
  \`accessories\`, \`physical_condition\`, \`notes\`, \`status\`, \`service_type\`, \`stage\`,
  \`total_price\`, \`labor_cost\`, \`discount\`, \`rounding_adjustment\`, \`vat_rate\`, \`is_vat_included\`,
  \`assigned_technician_id\`, \`assigned_technician_name\`,
  \`is_contracted\`, \`contracted_company_id\`, \`contracted_company_name\`, \`payment_status\`,
  \`cash_amount\`, \`card_amount\`, \`transfer_amount\`, \`delivery_date\`,
  \`created_at\`, \`updated_at\`, \`data_json\`
) VALUES\n`;

    const ticketRows = safeTickets.map((t: any) => {
      const cleanJson = JSON.stringify(t);
      const issue = t.issueDescription || t.issue || t.problemDescription || '';
      const accessories = Array.isArray(t.includedAccessories)
        ? t.includedAccessories.join(', ')
        : (t.accessories || '');
      const notes = t.technicianNotes || t.performedActions || t.notes || '';
      const totalPrice = getTicketCalculatedTotal(t);
      const payments = getTicketPayments(t);
      const isContracted = t.serviceType === 'Sozlesmeli' || Boolean(t.contractedCompanyId) || t.isContracted ? 1 : 0;
      const stage = t.stage || (t.status === 'TeslimEdildi' ? 'delivered' : t.status === 'Hazir' ? 'ready' : t.status === 'Tamirde' ? 'repair' : 'intake');

      return `(${escapeSqlString(t.id)}, ${escapeSqlString(t.customerName)}, ${escapeSqlString(t.customerPhone)}, ${escapeSqlString(t.customerEmail)}, ${escapeSqlString(t.customerAddress)}, ` +
        `${escapeSqlString(t.deviceType)}, ${escapeSqlString(t.deviceBrand)}, ${escapeSqlString(t.deviceModel)}, ${escapeSqlString(t.serialNumber || t.imei || '')}, ${escapeSqlString(issue)}, ` +
        `${escapeSqlString(accessories)}, ${escapeSqlString(t.physicalCondition || '')}, ${escapeSqlString(notes)}, ${escapeSqlString(t.status || 'Kaydedildi')}, ${escapeSqlString(t.serviceType || 'Ucretli')}, ${escapeSqlString(stage)}, ` +
        `${escapeSqlNumber(totalPrice)}, ${escapeSqlNumber(t.laborCost || 0)}, ${escapeSqlNumber(t.discount || 0)}, ${escapeSqlNumber(t.roundingAdjustment || 0)}, ${escapeSqlNumber(t.vatRate || 0)}, ${t.isVatIncluded ? 1 : 0}, ` +
        `${escapeSqlString(t.assignedTechnicianId)}, ${escapeSqlString(t.assignedTechnicianName)}, ` +
        `${isContracted}, ${escapeSqlString(t.contractedCompanyId)}, ${escapeSqlString(t.contractedCompanyName)}, ${escapeSqlString(t.paymentStatus || 'Bekliyor')}, ` +
        `${escapeSqlNumber(payments.cash)}, ${escapeSqlNumber(payments.card)}, ${escapeSqlNumber(payments.transfer)}, ${escapeSqlDate(t.deliveryDate)}, ` +
        `${escapeSqlDate(t.createdAt)}, ${escapeSqlDate(t.updatedAt || t.createdAt)}, ${escapeSqlString(cleanJson)})`;
    });
    sql += ticketRows.join(',\n') + `;\n\n`;
  }

  // 1.1 TICKET PARTS SUB-TABLE
  sql += `-- --------------------------------------------------------\n`;
  sql += `-- 1.1 Servis Kayıtlarında Kullanılan Parçalar (\`ticket_parts\`)\n`;
  sql += `-- --------------------------------------------------------\n`;
  sql += `CREATE TABLE IF NOT EXISTS \`ticket_parts\` (
  \`id\` VARCHAR(64) NOT NULL,
  \`ticket_id\` VARCHAR(64) NOT NULL,
  \`item_id\` VARCHAR(64) NULL,
  \`name\` VARCHAR(255) NOT NULL,
  \`quantity\` INT NOT NULL DEFAULT 1,
  \`unit_price\` DECIMAL(12, 2) DEFAULT 0.00,
  \`cost_price\` DECIMAL(12, 2) DEFAULT 0.00,
  \`currency\` VARCHAR(10) DEFAULT 'TRY',
  \`is_out_of_contract\` TINYINT(1) DEFAULT 0,
  PRIMARY KEY (\`id\`),
  INDEX \`idx_tp_ticket_id\` (\`ticket_id\`),
  INDEX \`idx_tp_item_id\` (\`item_id\`)
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;\n\n`;

  const allTicketParts: any[] = [];
  safeTickets.forEach((t: any) => {
    if (Array.isArray(t.parts)) {
      t.parts.forEach((p: any, idx: number) => {
        allTicketParts.push({
          id: p.id || `TP-${t.id}-${idx + 1}`,
          ticketId: t.id,
          itemId: p.itemId || p.partId || null,
          name: p.name || p.itemName || 'Yedek Parça',
          quantity: Number(p.quantity) || 1,
          unitPrice: Number(p.unitPrice) || 0,
          costPrice: Number(p.costPrice) || 0,
          currency: p.currency || 'TRY',
          isOutOfContract: p.isOutOfContract ? 1 : 0,
        });
      });
    }
  });

  if (allTicketParts.length > 0) {
    sql += `INSERT INTO \`ticket_parts\` (\`id\`, \`ticket_id\`, \`item_id\`, \`name\`, \`quantity\`, \`unit_price\`, \`cost_price\`, \`currency\`, \`is_out_of_contract\`) VALUES\n`;
    sql += allTicketParts.map(tp => `(${escapeSqlString(tp.id)}, ${escapeSqlString(tp.ticketId)}, ${escapeSqlString(tp.itemId)}, ${escapeSqlString(tp.name)}, ${escapeSqlNumber(tp.quantity, 1)}, ${escapeSqlNumber(tp.unitPrice)}, ${escapeSqlNumber(tp.costPrice)}, ${escapeSqlString(tp.currency)}, ${tp.isOutOfContract})`).join(',\n') + `;\n\n`;
  }

  // 1.2 TICKET PAYMENTS SUB-TABLE
  sql += `-- --------------------------------------------------------\n`;
  sql += `-- 1.2 Servis Tahsilat Hareketleri (\`ticket_payments\`)\n`;
  sql += `-- --------------------------------------------------------\n`;
  sql += `CREATE TABLE IF NOT EXISTS \`ticket_payments\` (
  \`id\` VARCHAR(64) NOT NULL,
  \`ticket_id\` VARCHAR(64) NOT NULL,
  \`amount\` DECIMAL(12, 2) NOT NULL DEFAULT 0.00,
  \`method\` VARCHAR(50) NOT NULL DEFAULT 'Nakit',
  \`payment_date\` DATETIME NULL,
  \`notes\` TEXT NULL,
  PRIMARY KEY (\`id\`),
  INDEX \`idx_pay_ticket_id\` (\`ticket_id\`)
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;\n\n`;

  const allTicketPayments: any[] = [];
  safeTickets.forEach((t: any) => {
    if (Array.isArray(t.payments)) {
      t.payments.forEach((p: any, idx: number) => {
        allTicketPayments.push({
          id: p.id || `PAY-${t.id}-${idx + 1}`,
          ticketId: t.id,
          amount: Number(p.amount) || 0,
          method: p.method || 'Nakit',
          date: p.date || p.createdAt || t.createdAt,
          notes: p.notes || p.note || '',
        });
      });
    }
  });

  if (allTicketPayments.length > 0) {
    sql += `INSERT INTO \`ticket_payments\` (\`id\`, \`ticket_id\`, \`amount\`, \`method\`, \`payment_date\`, \`notes\`) VALUES\n`;
    sql += allTicketPayments.map(tp => `(${escapeSqlString(tp.id)}, ${escapeSqlString(tp.ticketId)}, ${escapeSqlNumber(tp.amount)}, ${escapeSqlString(tp.method)}, ${escapeSqlDate(tp.date)}, ${escapeSqlString(tp.notes)})`).join(',\n') + `;\n\n`;
  }

  // 2. INVENTORY TABLE
  sql += `-- --------------------------------------------------------\n`;
  sql += `-- 2. Yedek Parça ve Envanter Tablosu (\`inventory\`)\n`;
  sql += `-- --------------------------------------------------------\n`;
  sql += `CREATE TABLE IF NOT EXISTS \`inventory\` (
  \`id\` VARCHAR(64) NOT NULL,
  \`name\` VARCHAR(255) NOT NULL,
  \`code\` VARCHAR(100) NULL,
  \`barcode\` VARCHAR(100) NULL,
  \`category\` VARCHAR(100) NULL,
  \`brand\` VARCHAR(100) NULL,
  \`model\` VARCHAR(100) NULL,
  \`stock\` INT DEFAULT 0,
  \`min_stock\` INT DEFAULT 0,
  \`cost_try\` DECIMAL(12, 2) DEFAULT 0.00,
  \`cost_usd\` DECIMAL(12, 2) DEFAULT 0.00,
  \`sale_price_try\` DECIMAL(12, 2) DEFAULT 0.00,
  \`sale_price_usd\` DECIMAL(12, 2) DEFAULT 0.00,
  \`currency\` VARCHAR(10) DEFAULT 'TRY',
  \`shelf_location\` VARCHAR(100) NULL,
  \`supplier\` VARCHAR(255) NULL,
  \`created_at\` DATETIME NULL,
  \`updated_at\` DATETIME NULL,
  \`data_json\` LONGTEXT NULL,
  PRIMARY KEY (\`id\`),
  INDEX \`idx_inv_category\` (\`category\`),
  INDEX \`idx_inv_code\` (\`code\`)
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;\n\n`;

  if (safeInventory.length > 0) {
    sql += `INSERT INTO \`inventory\` (
  \`id\`, \`name\`, \`code\`, \`barcode\`, \`category\`, \`brand\`, \`model\`,
  \`stock\`, \`min_stock\`, \`cost_try\`, \`cost_usd\`, \`sale_price_try\`, \`sale_price_usd\`,
  \`currency\`, \`shelf_location\`, \`supplier\`,
  \`created_at\`, \`updated_at\`, \`data_json\`
) VALUES\n`;

    const invRows = safeInventory.map((item: any) => {
      const cleanJson = JSON.stringify(item);
      const shelf = item.shelfLocation || item.location || '';
      const salePrice = item.salePrice !== undefined ? item.salePrice : (item.sellingPrice || 0);

      return `(${escapeSqlString(item.id)}, ${escapeSqlString(item.name)}, ${escapeSqlString(item.code || item.id)}, ${escapeSqlString(item.barcode || item.code || item.id)}, ${escapeSqlString(item.category || 'Genel')}, ${escapeSqlString(item.brand || '')}, ${escapeSqlString(item.model || '')}, ` +
        `${escapeSqlNumber(item.quantity)}, ${escapeSqlNumber(item.minQuantity)}, ${escapeSqlNumber(item.costPrice || 0)}, ${escapeSqlNumber(item.costPriceUSD || 0)}, ${escapeSqlNumber(salePrice)}, ${escapeSqlNumber(item.salePriceUSD || 0)}, ` +
        `${escapeSqlString(item.currency || 'TRY')}, ${escapeSqlString(shelf)}, ${escapeSqlString(item.supplier || '')}, ` +
        `${escapeSqlDate(item.createdAt)}, ${escapeSqlDate(item.updatedAt || item.createdAt)}, ${escapeSqlString(cleanJson)})`;
    });
    sql += invRows.join(',\n') + `;\n\n`;
  }

  // 2.1 INVENTORY LOTS SUB-TABLE
  sql += `-- --------------------------------------------------------\n`;
  sql += `-- 2.1 Envanter Stok Alım Partileri (\`inventory_lots\`)\n`;
  sql += `-- --------------------------------------------------------\n`;
  sql += `CREATE TABLE IF NOT EXISTS \`inventory_lots\` (
  \`id\` VARCHAR(64) NOT NULL,
  \`item_id\` VARCHAR(64) NOT NULL,
  \`quantity\` INT NOT NULL DEFAULT 0,
  \`remaining_quantity\` INT NOT NULL DEFAULT 0,
  \`cost_price_try\` DECIMAL(12, 2) DEFAULT 0.00,
  \`cost_price_usd\` DECIMAL(12, 2) DEFAULT 0.00,
  \`currency\` VARCHAR(10) DEFAULT 'TRY',
  \`purchase_date\` DATETIME NULL,
  \`supplier\` VARCHAR(255) NULL,
  \`note\` TEXT NULL,
  PRIMARY KEY (\`id\`),
  INDEX \`idx_lot_item_id\` (\`item_id\`)
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;\n\n`;

  const allLots: any[] = [];
  safeInventory.forEach((item: any) => {
    if (Array.isArray(item.lots)) {
      item.lots.forEach((lot: any, idx: number) => {
        allLots.push({
          id: lot.id || `LOT-${item.id}-${idx + 1}`,
          itemId: item.id,
          quantity: Number(lot.quantity) || 0,
          remainingQuantity: Number(lot.remainingQuantity) || 0,
          costPrice: Number(lot.costPrice) || 0,
          costPriceUSD: Number(lot.costPriceUSD) || 0,
          currency: lot.currency || 'TRY',
          purchaseDate: lot.purchaseDate || lot.createdAt || item.createdAt,
          supplier: lot.supplier || item.supplier || '',
          note: lot.note || '',
        });
      });
    }
  });

  if (allLots.length > 0) {
    sql += `INSERT INTO \`inventory_lots\` (\`id\`, \`item_id\`, \`quantity\`, \`remaining_quantity\`, \`cost_price_try\`, \`cost_price_usd\`, \`currency\`, \`purchase_date\`, \`supplier\`, \`note\`) VALUES\n`;
    sql += allLots.map(l => `(${escapeSqlString(l.id)}, ${escapeSqlString(l.itemId)}, ${escapeSqlNumber(l.quantity)}, ${escapeSqlNumber(l.remainingQuantity)}, ${escapeSqlNumber(l.costPrice)}, ${escapeSqlNumber(l.costPriceUSD)}, ${escapeSqlString(l.currency)}, ${escapeSqlDate(l.purchaseDate)}, ${escapeSqlString(l.supplier)}, ${escapeSqlString(l.note)})`).join(',\n') + `;\n\n`;
  }

  // 3. STOCK MOVEMENTS TABLE
  sql += `-- --------------------------------------------------------\n`;
  sql += `-- 3. Stok Hareketleri Tablosu (\`stock_movements\`)\n`;
  sql += `-- --------------------------------------------------------\n`;
  sql += `CREATE TABLE IF NOT EXISTS \`stock_movements\` (
  \`id\` VARCHAR(64) NOT NULL,
  \`part_id\` VARCHAR(64) NULL,
  \`part_name\` VARCHAR(255) NULL,
  \`item_code\` VARCHAR(100) NULL,
  \`type\` VARCHAR(50) NOT NULL,
  \`quantity\` INT NOT NULL DEFAULT 0,
  \`previous_quantity\` INT DEFAULT 0,
  \`new_quantity\` INT DEFAULT 0,
  \`unit_cost\` DECIMAL(12, 2) DEFAULT 0.00,
  \`unit_price\` DECIMAL(12, 2) DEFAULT 0.00,
  \`ticket_id\` VARCHAR(64) NULL,
  \`user_name\` VARCHAR(100) NULL,
  \`notes\` TEXT NULL,
  \`created_at\` DATETIME NULL,
  \`data_json\` LONGTEXT NULL,
  PRIMARY KEY (\`id\`),
  INDEX \`idx_sm_part_id\` (\`part_id\`),
  INDEX \`idx_sm_ticket_id\` (\`ticket_id\`),
  INDEX \`idx_sm_created_at\` (\`created_at\`)
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;\n\n`;

  if (safeStockMovements.length > 0) {
    sql += `INSERT INTO \`stock_movements\` (
  \`id\`, \`part_id\`, \`part_name\`, \`item_code\`, \`type\`, \`quantity\`,
  \`previous_quantity\`, \`new_quantity\`, \`unit_cost\`, \`unit_price\`,
  \`ticket_id\`, \`user_name\`, \`notes\`, \`created_at\`, \`data_json\`
) VALUES\n`;

    const smRows = safeStockMovements.map((sm: any) => {
      const cleanJson = JSON.stringify(sm);
      const note = sm.note || sm.reason || sm.notes || '';
      return `(${escapeSqlString(sm.id)}, ${escapeSqlString(sm.itemId || sm.partId)}, ${escapeSqlString(sm.itemName || sm.partName)}, ${escapeSqlString(sm.itemCode || '')}, ${escapeSqlString(sm.type)}, ${escapeSqlNumber(sm.quantity)}, ` +
        `${escapeSqlNumber(sm.previousQuantity)}, ${escapeSqlNumber(sm.newQuantity)}, ${escapeSqlNumber(sm.unitCost)}, ${escapeSqlNumber(sm.unitPrice)}, ` +
        `${escapeSqlString(sm.ticketId)}, ${escapeSqlString(sm.userName || sm.technicianName)}, ${escapeSqlString(note)}, ${escapeSqlDate(sm.date || sm.createdAt)}, ${escapeSqlString(cleanJson)})`;
    });
    sql += smRows.join(',\n') + `;\n\n`;
  }

  // 4. CONTRACTED COMPANIES TABLE
  sql += `-- --------------------------------------------------------\n`;
  sql += `-- 4. Sözleşmeli Firmalar & Cariler Tablosu (\`contracted_companies\`)\n`;
  sql += `-- --------------------------------------------------------\n`;
  sql += `CREATE TABLE IF NOT EXISTS \`contracted_companies\` (
  \`id\` VARCHAR(64) NOT NULL,
  \`name\` VARCHAR(255) NOT NULL,
  \`phone\` VARCHAR(64) NULL,
  \`email\` VARCHAR(255) NULL,
  \`address\` TEXT NULL,
  \`authorized_person\` VARCHAR(255) NULL,
  \`tax_office\` VARCHAR(100) NULL,
  \`tax_number\` VARCHAR(100) NULL,
  \`discount_rate\` DECIMAL(5, 2) DEFAULT 0.00,
  \`balance\` DECIMAL(12, 2) DEFAULT 0.00,
  \`is_active\` TINYINT(1) DEFAULT 1,
  \`created_at\` DATETIME NULL,
  \`updated_at\` DATETIME NULL,
  \`data_json\` LONGTEXT NULL,
  PRIMARY KEY (\`id\`),
  INDEX \`idx_cc_name\` (\`name\`(50)),
  INDEX \`idx_cc_phone\` (\`phone\`)
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;\n\n`;

  if (safeContracted.length > 0) {
    sql += `INSERT INTO \`contracted_companies\` (
  \`id\`, \`name\`, \`phone\`, \`email\`, \`address\`,
  \`authorized_person\`, \`tax_office\`, \`tax_number\`, \`discount_rate\`, \`balance\`,
  \`is_active\`, \`created_at\`, \`updated_at\`, \`data_json\`
) VALUES\n`;

    const ccRows = safeContracted.map((cc: any) => {
      const cleanJson = JSON.stringify(cc);
      const authPerson = cc.authorizedPerson || cc.contactPerson || '';
      return `(${escapeSqlString(cc.id)}, ${escapeSqlString(cc.name)}, ${escapeSqlString(cc.phone)}, ${escapeSqlString(cc.email)}, ${escapeSqlString(cc.address)}, ` +
        `${escapeSqlString(authPerson)}, ${escapeSqlString(cc.taxOffice)}, ${escapeSqlString(cc.taxNumber)}, ${escapeSqlNumber(cc.discountRate)}, ${escapeSqlNumber(cc.balance)}, ` +
        `${cc.isActive !== false ? 1 : 0}, ${escapeSqlDate(cc.createdAt)}, ${escapeSqlDate(cc.updatedAt || cc.createdAt)}, ${escapeSqlString(cleanJson)})`;
    });
    sql += ccRows.join(',\n') + `;\n\n`;
  }

  // 4.1 CONTRACTED COMPANY PAYMENTS SUB-TABLE
  sql += `-- --------------------------------------------------------\n`;
  sql += `-- 4.1 Cari Firma Tahsilat ve Ödemeleri (\`contracted_company_payments\`)\n`;
  sql += `-- --------------------------------------------------------\n`;
  sql += `CREATE TABLE IF NOT EXISTS \`contracted_company_payments\` (
  \`id\` VARCHAR(64) NOT NULL,
  \`company_id\` VARCHAR(64) NOT NULL,
  \`amount\` DECIMAL(12, 2) NOT NULL DEFAULT 0.00,
  \`payment_date\` DATETIME NULL,
  \`method\` VARCHAR(50) DEFAULT 'Nakit',
  \`notes\` TEXT NULL,
  PRIMARY KEY (\`id\`),
  INDEX \`idx_ccp_company_id\` (\`company_id\`)
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;\n\n`;

  const allCompanyPayments: any[] = [];
  safeContracted.forEach((cc: any) => {
    if (Array.isArray(cc.payments)) {
      cc.payments.forEach((p: any, idx: number) => {
        allCompanyPayments.push({
          id: p.id || `CCP-${cc.id}-${idx + 1}`,
          companyId: cc.id,
          amount: Number(p.amount) || 0,
          date: p.date || p.createdAt,
          method: p.method || 'Nakit',
          notes: p.notes || p.description || '',
        });
      });
    }
  });

  if (allCompanyPayments.length > 0) {
    sql += `INSERT INTO \`contracted_company_payments\` (\`id\`, \`company_id\`, \`amount\`, \`payment_date\`, \`method\`, \`notes\`) VALUES\n`;
    sql += allCompanyPayments.map(cp => `(${escapeSqlString(cp.id)}, ${escapeSqlString(cp.companyId)}, ${escapeSqlNumber(cp.amount)}, ${escapeSqlDate(cp.date)}, ${escapeSqlString(cp.method)}, ${escapeSqlString(cp.notes)})`).join(',\n') + `;\n\n`;
  }

  // 5. USERS TABLE
  sql += `-- --------------------------------------------------------\n`;
  sql += `-- 5. Kullanıcılar ve Personel Tablosu (\`users\`)\n`;
  sql += `-- --------------------------------------------------------\n`;
  sql += `CREATE TABLE IF NOT EXISTS \`users\` (
  \`id\` VARCHAR(64) NOT NULL,
  \`username\` VARCHAR(100) NOT NULL,
  \`password\` VARCHAR(255) NOT NULL,
  \`name\` VARCHAR(255) NOT NULL,
  \`role\` VARCHAR(50) DEFAULT 'technician',
  \`phone\` VARCHAR(64) NULL,
  \`is_active\` TINYINT(1) DEFAULT 1,
  \`created_at\` DATETIME NULL,
  \`updated_at\` DATETIME NULL,
  \`data_json\` LONGTEXT NULL,
  PRIMARY KEY (\`id\`),
  UNIQUE KEY \`idx_user_username\` (\`username\`)
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;\n\n`;

  if (safeUsers.length > 0) {
    sql += `INSERT INTO \`users\` (
  \`id\`, \`username\`, \`password\`, \`name\`, \`role\`,
  \`phone\`, \`is_active\`, \`created_at\`, \`updated_at\`, \`data_json\`
) VALUES\n`;

    const userRows = safeUsers.map(u => {
      const cleanJson = JSON.stringify(u);
      return `(${escapeSqlString(u.id)}, ${escapeSqlString(u.username)}, ${escapeSqlString(u.password || '')}, ${escapeSqlString(u.name)}, ${escapeSqlString(u.role || 'technician')}, ` +
        `${escapeSqlString(u.phone)}, ${u.isActive !== false ? 1 : 0}, ${escapeSqlDate(u.createdAt)}, ${escapeSqlDate(u.lastLogin || u.createdAt)}, ${escapeSqlString(cleanJson)})`;
    });
    sql += userRows.join(',\n') + `;\n\n`;
  }

  // 6. SMS LOGS TABLE
  sql += `-- --------------------------------------------------------\n`;
  sql += `-- 6. SMS Gönderim Logları Tablosu (\`sms_logs\`)\n`;
  sql += `-- --------------------------------------------------------\n`;
  sql += `CREATE TABLE IF NOT EXISTS \`sms_logs\` (
  \`id\` VARCHAR(64) NOT NULL,
  \`ticket_id\` VARCHAR(64) NULL,
  \`phone\` VARCHAR(255) NULL,
  \`customer_name\` VARCHAR(255) NULL,
  \`message\` TEXT NULL,
  \`status\` VARCHAR(50) NULL,
  \`response_code\` VARCHAR(50) NULL,
  \`response_message\` TEXT NULL,
  \`order_id\` VARCHAR(100) NULL,
  \`created_at\` DATETIME NULL,
  PRIMARY KEY (\`id\`),
  INDEX \`idx_sms_ticket\` (\`ticket_id\`),
  INDEX \`idx_sms_created_at\` (\`created_at\`)
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;\n\n`;

  if (safeSmsLogs.length > 0) {
    sql += `INSERT INTO \`sms_logs\` (
  \`id\`, \`ticket_id\`, \`phone\`, \`customer_name\`, \`message\`,
  \`status\`, \`response_code\`, \`response_message\`, \`order_id\`, \`created_at\`
) VALUES\n`;

    const smsRows = safeSmsLogs.map(sl => {
      return `(${escapeSqlString(sl.id)}, ${escapeSqlString(sl.ticketId)}, ${escapeSqlString(sl.phone)}, ${escapeSqlString(sl.customerName)}, ${escapeSqlString(sl.message)}, ` +
        `${escapeSqlString(sl.status)}, ${escapeSqlString(sl.responseCode)}, ${escapeSqlString(sl.responseMessage)}, ${escapeSqlString(sl.orderId)}, ${escapeSqlDate(sl.date || sl.created_at || sl.createdAt)})`;
    });
    sql += smsRows.join(',\n') + `;\n\n`;
  }

  // 7. SETTINGS TABLE
  sql += `-- --------------------------------------------------------\n`;
  sql += `-- 7. Sistem Ayarları Tablosu (\`settings\`)\n`;
  sql += `-- --------------------------------------------------------\n`;
  sql += `CREATE TABLE IF NOT EXISTS \`settings\` (
  \`setting_key\` VARCHAR(100) NOT NULL,
  \`setting_value\` LONGTEXT NULL,
  \`updated_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`setting_key\`)
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;\n\n`;

  const settingsToInsert: Array<{ key: string; val: any }> = [
    { key: 'company', val: data.company },
    { key: 'deviceTypes', val: data.deviceTypes },
    { key: 'deviceBrands', val: data.deviceBrands },
    { key: 'inventoryCategories', val: data.inventoryCategories },
    { key: 'labelConfig', val: data.labelConfig },
    { key: 'smsSettings', val: data.smsSettings },
    { key: 'exchangeRate', val: data.exchangeRate },
  ];

  sql += `INSERT INTO \`settings\` (\`setting_key\`, \`setting_value\`) VALUES\n`;
  const setRows = settingsToInsert.map(s => {
    return `(${escapeSqlString(s.key)}, ${escapeSqlString(JSON.stringify(s.val || {}))})`;
  });
  sql += setRows.join(',\n') + `\nON DUPLICATE KEY UPDATE \`setting_value\` = VALUES(\`setting_value\`);\n\n`;

  // 8. APP_DATA (BLOB BACKUP)
  sql += `-- --------------------------------------------------------\n`;
  sql += `-- 8. Hızlı JSON Veri Depo Tablosu (\`app_data\`)\n`;
  sql += `-- --------------------------------------------------------\n`;
  sql += `CREATE TABLE IF NOT EXISTS \`app_data\` (
  \`id\` VARCHAR(50) NOT NULL,
  \`data_json\` LONGTEXT NOT NULL,
  \`updated_at\` TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP,
  PRIMARY KEY (\`id\`)
) ENGINE=InnoDB DEFAULT CHARSET=utf8mb4 COLLATE=utf8mb4_unicode_ci;\n\n`;

  sql += `INSERT INTO \`app_data\` (\`id\`, \`data_json\`) VALUES\n`;
  sql += `('main_store', ${escapeSqlString(JSON.stringify(data))})\n`;
  sql += `ON DUPLICATE KEY UPDATE \`data_json\` = VALUES(\`data_json\`);\n\n`;

  sql += `COMMIT;\n`;
  return sql;
}

export function downloadSqlFile(data: FullDatabaseBackup, filename?: string) {
  const sqlContent = generateSqlDump(data);
  const blob = new Blob([sqlContent], { type: 'application/sql;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `teknik_servis_mysql_${new Date().toISOString().slice(0, 10)}.sql`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadSqlDump(filename?: string) {
  try {
    // Attempt 1: Fetch direct generated SQL from backend
    const resp = await fetch('/api/export-sql', { cache: 'no-store' });
    if (resp.ok) {
      const sqlText = await resp.text();
      if (sqlText && sqlText.includes('CREATE TABLE')) {
        const blob = new Blob([sqlText], { type: 'application/sql;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `teknik_servis_mysql_${new Date().toISOString().slice(0, 10)}.sql`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return;
      }
    }
  } catch {}

  // Fallback: Generate client-side using full synced database snapshot
  const { getFullDatabaseData } = await import('./storage');
  const fullData = await getFullDatabaseData();
  downloadSqlFile(fullData, filename);
}
